import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Clock, Calendar, ArrowLeft, Share2, Twitter, Linkedin, Facebook, Hash, Loader2, Eye } from 'lucide-react';
import BlogComments from '../components/blog/BlogComments';
import { TiltCard } from '../components/home/TiltCard';
import { ScrollDepthReveal } from '../components/home/ScrollDepthReveal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const setMetaTag = (attr, name, content) => {
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content || '');
};

const useBlogSeoHead = (post) => {
  useEffect(() => {
    if (!post) return;
    const title = post.seo_title || post.title;
    document.title = `${title} | Bivas Mishra`;
    setMetaTag('name', 'description', post.seo_description || '');
    setMetaTag('name', 'robots', post.is_indexable === false ? 'noindex, nofollow' : 'index, follow');
    setMetaTag('property', 'og:title', post.og_title || post.seo_title || post.title);
    setMetaTag('property', 'og:description', post.og_description || post.seo_description || '');
    setMetaTag('property', 'og:image', post.featured_image || '');
    setMetaTag('property', 'og:type', 'article');

    let ldScript = document.getElementById('blog-jsonld');
    if (post.enable_rich_results !== false) {
      if (!ldScript) {
        ldScript = document.createElement('script');
        ldScript.type = 'application/ld+json';
        ldScript.id = 'blog-jsonld';
        document.head.appendChild(ldScript);
      }
      ldScript.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        image: [post.featured_image],
        datePublished: post.created_at,
        dateModified: post.updated_at,
        author: { '@type': 'Person', name: post.author_display || post.author_name },
        description: post.seo_description || '',
      });
    } else if (ldScript) {
      ldScript.remove();
    }

    return () => {
      document.title = 'Bivas Mishra';
      const ld = document.getElementById('blog-jsonld');
      if (ld) ld.remove();
    };
  }, [post]);
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchPost = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const r = await fetch(`${API_URL}/api/blogs/${slug}`, { credentials: 'include' });
        if (!active) return;
        if (r.ok) {
          setPost(await r.json());
        } else {
          setPost(null);
          setNotFound(true);
        }
      } catch (e) {
        if (active) { setPost(null); setNotFound(true); }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchPost();
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    let active = true;
    fetch(`${API_URL}/api/blogs`)
      .then((r) => (r.ok ? r.json() : []))
      .then((all) => {
        if (active) setRelated(all.filter((p) => p.slug !== post.slug).slice(0, 3));
      })
      .catch(() => {});
    return () => { active = false; };
  }, [post]);

  useBlogSeoHead(post);

  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? Math.min(100, Math.max(0, (window.scrollY / docHeight) * 100)) : 0);
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0c]">
        <Header />
        <main className="pt-32 pb-20 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-neutral-400" />
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-[#0a0a0c]">
        <Header />
        <main className="pt-32 pb-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Post Not Found</h1>
            <p className="text-neutral-400 mb-8">The article you're looking for doesn't exist.</p>
            <Link to="/blog" className="inline-flex items-center px-6 py-3 bg-white text-black rounded-full hover:shadow-lg hover:bg-neutral-200 transition-all">
              <ArrowLeft size={18} className="mr-2" />Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const authorName = post.author_display || post.author_name || 'Bivas Mishra';

  return (
    <div className="min-h-screen bg-[#0a0a0c]" data-testid="blog-post-page">
      <div
        data-testid="reading-progress-bar"
        className="fixed top-0 left-0 h-1 bg-white z-[60] transition-all duration-150"
        style={{ width: `${progress}%` }}
      />
      <Header />
      <main className="pt-20">
        <section className="pt-10 pb-4 bg-[#0a0a0c]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-4 mb-6">
              <Link to="/blog" className="inline-flex items-center text-neutral-400 hover:text-white transition-colors text-sm font-medium">
                <ArrowLeft size={16} className="mr-2" />Back to Blog
              </Link>
              <span className="inline-block px-4 py-1.5 bg-white text-black text-sm font-bold rounded-full shadow-md">
                {post.category}
              </span>
            </div>
            <h1 data-testid="blog-post-title" className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-neutral-400 text-sm mb-8">
              <div className="flex items-center bg-white/5 px-4 py-2 rounded-full border border-white/10">
                <img src={post.author_avatar || 'https://via.placeholder.com/40'} alt={authorName} className="w-8 h-8 rounded-full border border-white/10 mr-3" />
                <span className="font-semibold text-white">{authorName}</span>
              </div>
              <div className="flex items-center bg-white/5 px-4 py-2 rounded-full border border-white/10">
                <Calendar size={15} className="mr-2" />{formatDate(post.created_at)}
              </div>
              <div data-testid="blog-post-read-time" className="flex items-center bg-white/5 px-4 py-2 rounded-full border border-white/10">
                <Clock size={15} className="mr-2" />{post.read_time}
              </div>
              {post.show_views !== false && (
                <div className="flex items-center bg-white/5 px-4 py-2 rounded-full border border-white/10">
                  <Eye size={15} className="mr-2" />{post.views_count || 0} views
                </div>
              )}
            </div>
            <TiltCard maxTilt={4} hoverScale={1.005} testId="blog-featured-image-tilt">
              <div className="rounded-2xl overflow-hidden shadow-2xl shadow-black/40 aspect-video bg-neutral-900">
                <img src={post.featured_image} alt={post.featured_image_alt || post.title} className="w-full h-full object-cover" />
              </div>
            </TiltCard>
          </div>
        </section>

        <section className="py-12 lg:py-16 bg-[#0a0a0c]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div data-testid="blog-share-sidebar" className="hidden xl:flex xl:flex-col gap-3 absolute -left-20 top-0 w-14">
              <p className="text-xs text-neutral-500 font-bold mb-1 tracking-wide text-center">SHARE</p>
              {[
                { Icon: Twitter, url: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${post.title}` },
                { Icon: Linkedin, url: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}` },
                { Icon: Facebook, url: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}` },
              ].map(({ Icon, url }, i) => (
                <button key={i} onClick={() => window.open(url, '_blank')} className="p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white hover:border-transparent hover:text-black text-neutral-300 transition-all duration-300 shadow-sm hover:shadow-lg">
                  <Icon size={18} />
                </button>
              ))}
            </div>

            <article>
              {/* Article body kept white for prose readability */}
              <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-2xl shadow-black/40 border border-white/10">
                  <div
                    data-testid="blog-post-content"
                    className="prose prose-lg max-w-none tiptap-content
                      prose-headings:font-bold
                      prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-5 prose-h2:text-gray-900 prose-h2:border-l-4 prose-h2:border-neutral-800 prose-h2:pl-5 prose-h2:py-2 prose-h2:bg-neutral-50 prose-h2:rounded-r-lg
                      prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-neutral-800 prose-h3:font-bold
                      prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-3 prose-h4:text-gray-900 prose-h4:font-semibold
                      prose-p:text-gray-900 prose-p:leading-8 prose-p:mb-5 prose-p:text-[17px] prose-p:font-normal
                      prose-li:text-gray-900 prose-li:my-2 prose-li:leading-7 prose-li:text-[16px]
                      prose-strong:text-black prose-strong:font-bold
                      prose-a:text-neutral-800 prose-a:font-semibold prose-a:no-underline hover:prose-a:text-black hover:prose-a:underline
                      prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6 prose-ul:space-y-2 prose-ul:bg-gray-50 prose-ul:py-4 prose-ul:pr-4 prose-ul:rounded-xl prose-ul:border-l-4 prose-ul:border-neutral-400
                      prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6 prose-ol:space-y-2 prose-ol:bg-neutral-50 prose-ol:py-4 prose-ol:pr-4 prose-ol:rounded-xl prose-ol:border-l-4 prose-ol:border-neutral-500"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />
                </div>

                {post.hashtags?.length > 0 && (
                  <div className="mt-8 p-6 bg-white/[0.04] rounded-2xl border border-white/10 shadow-lg">
                    <div className="flex items-center mb-5">
                      <div className="p-2.5 bg-white/10 rounded-xl mr-3 shadow-md">
                        <Hash size={20} className="text-white" />
                      </div>
                      <span className="font-bold text-white text-lg">Tags</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {post.hashtags.map((tag, index) => (
                        <span key={index} className="px-4 py-2 bg-white/5 text-neutral-300 text-sm font-semibold rounded-full border border-white/10 hover:bg-white hover:text-black hover:border-transparent transition-all duration-300 cursor-pointer">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-8 p-6 bg-white rounded-2xl shadow-xl">
                  <div className="flex items-center">
                    <img src={post.author_avatar || 'https://via.placeholder.com/64'} alt={authorName} className="w-16 h-16 rounded-full border-4 border-neutral-100 shadow-lg mr-5" />
                    <div>
                      <p className="text-sm text-neutral-500 font-medium mb-1">Written by</p>
                      <h3 className="text-xl font-bold text-black">{authorName}</h3>
                    </div>
                  </div>
                </div>

                {post.show_comments !== false && (
                  <BlogComments slug={post.slug} blogAuthorId={post.author_id} />
                )}

                <div data-testid="blog-share-mobile" className="xl:hidden mt-8 p-6 bg-white/[0.04] rounded-2xl border border-white/10 shadow-lg">
                  <div className="flex items-center mb-4">
                    <Share2 size={18} className="mr-2 text-neutral-300" />
                    <span className="text-white font-bold">Share this article</span>
                  </div>
                  <div className="flex gap-3">
                    {[
                      { Icon: Twitter, label: 'Twitter', url: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${post.title}` },
                      { Icon: Linkedin, label: 'LinkedIn', url: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}` },
                      { Icon: Facebook, label: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}` },
                    ].map(({ Icon, label, url }, i) => (
                      <button key={i} onClick={() => window.open(url, '_blank')} className="flex items-center px-4 py-2.5 bg-white text-black rounded-xl hover:bg-neutral-200 transition-all shadow-md font-medium">
                        <Icon size={16} className="mr-2" /><span className="text-sm">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
            </article>
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-16 lg:py-20 bg-black">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <ScrollDepthReveal className="text-center mb-10">
                <h2 className="text-2xl lg:text-3xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  More Articles You Might Enjoy
                </h2>
              </ScrollDepthReveal>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {related.map((relatedPost, index) => (
                  <ScrollDepthReveal key={relatedPost.blog_id} delay={Math.min(index * 0.08, 0.24)}>
                    <TiltCard maxTilt={10} hoverScale={1.04} testId={`related-blog-tilt-${relatedPost.slug}`}>
                      <Link to={`/blog/${relatedPost.slug}`} className="group block">
                        <div className="bg-white/[0.04] rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-white/10 group-hover:border-white/30">
                          <div className="h-48 overflow-hidden relative">
                            <img src={relatedPost.featured_image} alt={relatedPost.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                            <span className="absolute bottom-3 left-3 px-3 py-1.5 bg-white text-black text-xs font-bold rounded-full shadow-md">
                              {relatedPost.category}
                            </span>
                          </div>
                          <div className="p-5">
                            <h3 className="font-bold text-white group-hover:text-neutral-300 transition-colors line-clamp-2 mb-3 text-lg leading-snug">
                              {relatedPost.title}
                            </h3>
                            <p className="text-sm text-neutral-400 flex items-center font-medium">
                              <Clock size={14} className="mr-1.5 text-neutral-500" />{formatDate(relatedPost.created_at)}
                            </p>
                          </div>
                        </div>
                      </Link>
                    </TiltCard>
                  </ScrollDepthReveal>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;
